/**
 * Exchange trading URL builder
 * Handles URL generation for each exchange's spot trading interface
 * 
 * This module provides a unified interface for generating exchange trading URLs
 * based on exchange name and trading pair.
 */

// Exchange URL templates mapping
export const EXCHANGE_URLS: Record<string, string> = {
  'upbit': 'https://upbit.com/exchange?code=CRIX.UPBIT.{pair}',
  'bithumb': 'https://www.bithumb.com/trade/order/{pair}',
  'coinone': 'https://coinone.co.kr/exchange/trade/{base}/{quote}',
  'binance': 'https://www.binance.com/en/trade/{pair}',
  'htx': 'https://www.htx.com/trade/{pair}?type=spot',
  'gate.io': 'https://www.gate.io/trade/{pair}',
  'gateio': 'https://www.gate.io/trade/{pair}', // Alias for gate.io
  'kucoin': 'https://www.kucoin.com/trade/{pair}',
  'okx': 'https://www.okx.com/trade-spot/{pair}',
  'mexc': 'https://www.mexc.com/exchange/{pair}',
  'bybit': 'https://www.bybit.com/en/trade/spot/{base}/{quote}',
  'bitget': 'https://www.bitget.com/en/spot/{pair}',
  'coinbase': 'https://pro.coinbase.com/trade/{pair}'
};

/**
 * Get trading URL for a specific exchange and pair
 * @param exchange - Exchange name (case-insensitive)
 * @param pair - Trading pair in format "BTC/USDT"
 * @returns Full trading URL or null if exchange not supported
 */
export function getTradingUrl(exchange: string, pair: string): string | null {
  const exchangeLower = exchange.toLowerCase();
  const template = EXCHANGE_URLS[exchangeLower];
  
  if (!template) {
    console.warn(`No URL template found for exchange: ${exchange}`);
    return null;
  }
  
  // Parse the pair format (e.g., "BTC/USDT" -> base: "BTC", quote: "USDT")
  const [base, quote] = pair.split('/');
  
  if (!base || !quote) {
    console.warn(`Invalid pair format: ${pair}. Expected format: BASE/QUOTE`);
    return null;
  }
  
  // Format the pair based on exchange requirements
  let formattedPair: string;
  
  switch (exchangeLower) {
    case 'upbit':
      // Upbit uses KRW-BTC format (quote-base)
      formattedPair = `${quote}-${base}`;
      break;
      
    case 'bithumb':
      // Bithumb uses BTC_KRW format
      formattedPair = `${base}_${quote}`;
      break;
      
    case 'coinone':
      // Coinone uses separate base/quote in URL, both lowercase
      // URL will be handled specially below
      formattedPair = ''; // Not used for coinone
      break;
      
    case 'binance':
      // Binance uses BTC_USDT format
      formattedPair = `${base}_${quote}`;
      break;
      
    case 'htx':
      // HTX uses lowercase with underscore format (eth_usdt)
      formattedPair = `${base.toLowerCase()}_${quote.toLowerCase()}`;
      break;
      
    case 'gate.io':
    case 'gateio':
      // Gate.io uses BTC_USDT format
      formattedPair = `${base}_${quote}`;
      break;
      
    case 'kucoin':
      // KuCoin uses BTC-USDT format
      formattedPair = `${base}-${quote}`;
      break;
      
    case 'okx':
      // OKX uses lowercase btc-usdt format
      formattedPair = `${base.toLowerCase()}-${quote.toLowerCase()}`;
      break;
      
    case 'mexc':
      // MEXC uses BTC_USDT format
      formattedPair = `${base}_${quote}`;
      break;
      
    case 'bybit':
      // Bybit uses separate base/quote in URL path
      // URL will be handled specially with {base}/{quote} placeholders
      formattedPair = ''; // Not used for bybit
      break;
      
    case 'bitget':
      // Bitget uses BTCUSDT_SPBL format
      formattedPair = `${base}${quote}_SPBL`;
      break;
      
    case 'coinbase':
      // Coinbase uses BTC-USD format
      formattedPair = `${base}-${quote}`;
      break;
      
    default:
      formattedPair = `${base}_${quote}`;
  }
  
  // Replace all placeholders
  return template
    .replace('{pair}', formattedPair)
    .replace('{base}', base.toLowerCase())
    .replace('{quote}', quote.toLowerCase());
}

/**
 * Check if an exchange is supported
 * @param exchange - Exchange name (case-insensitive)
 * @returns true if exchange is supported, false otherwise
 */
export function isExchangeSupported(exchange: string): boolean {
  return exchange.toLowerCase() in EXCHANGE_URLS;
}

/**
 * Get list of all supported exchanges
 * @returns Array of supported exchange names
 */
export function getSupportedExchanges(): string[] {
  return Object.keys(EXCHANGE_URLS);
}

// Exchange enum index to name mapping (matches Rust Exchange enum order)
export const EXCHANGE_NAMES = [
  'upbit',    // 0
  'bithumb',  // 1
  'coinone',  // 2
  'binance',  // 3
  'htx',      // 4
  'gateio',   // 5 (mapped from GateIO)
  'kucoin',   // 6
  'okx',      // 7
  'mexc',     // 8
  'bybit',    // 9
  'bitget',   // 10
  'coinbase'  // 11
] as const;

/**
 * Get trading URL using exchange index from Rust enum
 * @param exchangeIndex - Exchange enum index (0-11)
 * @param base - Base currency (e.g., "BTC")
 * @param quote - Quote currency (e.g., "USDT")
 * @returns Full trading URL or null if exchange not found
 */
export function getExchangeSpotUrl(exchangeIndex: number, base: string, quote: string): string | null {
  const exchangeName = EXCHANGE_NAMES[exchangeIndex];
  if (!exchangeName) {
    console.warn(`Invalid exchange index: ${exchangeIndex}`);
    return null;
  }
  
  const pair = `${base}/${quote}`;
  return getTradingUrl(exchangeName, pair);
}

// Map exchange indices to logo files
const EXCHANGE_LOGOS: Record<number, string> = {
  0: '/logos/upbit.png',
  1: '/logos/bithumb.ico',
  2: '/logos/coinone.ico',
  3: '/logos/binance.png',
  4: '/logos/htx.png',
  5: '/logos/gateio.png',
  6: '/logos/kucoin.png',
  7: '/logos/okx.png',
  8: '/logos/mexc.ico',
  9: '/logos/bybit.ico',
  10: '/logos/bitget.png',
  11: '/logos/coinbase.png'
};

/**
 * Get the logo URL for an exchange by index
 * @param exchangeIndex - Exchange enum index (0-11)
 * @returns Logo URL path
 */
export function getExchangeLogo(exchangeIndex: number): string {
  return EXCHANGE_LOGOS[exchangeIndex] || '/logos/default.png';
}

/**
 * Get exchange index by name (case-insensitive)
 * @param exchangeName - Exchange name (e.g., "binance", "Binance")
 * @returns Exchange index or undefined if not found
 */
export function getExchangeIndexByName(exchangeName: string): number | undefined {
  const lowerName = exchangeName.toLowerCase();
  // Handle gate.io alias
  const normalizedName = lowerName === 'gate.io' ? 'gateio' : lowerName;
  const index = EXCHANGE_NAMES.findIndex(name => name === normalizedName);
  return index >= 0 ? index : undefined;
}

/**
 * Get the logo URL for an exchange by name
 * @param exchangeName - Exchange name (e.g., "binance", "Binance")
 * @returns Logo URL path
 */
export function getExchangeLogoByName(exchangeName: string): string {
  const index = getExchangeIndexByName(exchangeName);
  return index !== undefined ? getExchangeLogo(index) : '/logos/default.png';
}

// Legacy class-based interface (kept for backward compatibility)
export class ExchangeUrls {
  static getSpotTradeUrl = getExchangeSpotUrl;
}